import { environment } from '@environments/environment';
import { ENVIRONMENTS } from '@shared/constants';
import { Dict } from '@shared/models';

/** The fake backend (and thus this mock data) only runs in these envs. */
const MOCKS_ACTIVE = [ENVIRONMENTS.DEV, ENVIRONMENTS.PLAYWRIGHT].includes(
  environment.env
);

/**
 * Demo toggle for the white-label / mock data, driven by the environment flag
 * `useRealBrands`.
 *
 * When `true`, mock challenges and products use real grocery brands
 * (Coca-Cola, Tropicana, Barilla…) instead of random `faker` data — easier for
 * clients to project onto in sales-demo videos. When `false`/omitted, the
 * previous behaviour ("méthode actuelle") of random faker data is used.
 *
 * It is gated on {@link MOCKS_ACTIVE}: since real brands only ever feed mock
 * data, the flag is inert (and therefore harmless) on any non-mock build, so
 * production env files never need to carry it.
 *
 * Set it where it matters in `src/environments/environment*.ts`.
 */
export const USE_REAL_BRANDS =
  MOCKS_ACTIVE && (environment.useRealBrands ?? false);

/** Neutral white-label fallbacks used when a brand has no own asset yet. */
export const FALLBACK_LOGO_PATH = 'assets/images/default/logo.svg';
export const FALLBACK_BANNER_PATH = 'assets/images/default/banner.webp';
const EMPTY_BRAND_DESCRIPTION: Dict = { en: '', fr: '', es: '' };

/** A real brand used to dress up the demo data. */
export interface DemoBrand {
  /** Brand name — used as the accessible challenge name and scanner title. */
  name: string;
  /** Shown as the challenge title on the card (localised). */
  label: Dict;
  /** Longer scope text shown in some variants / the product-check modal. */
  description: Dict;
  /**
   * Round logo shown on the card header. Optional: drop a file under
   * `src/assets/images/brands/` and set the path here, e.g.
   * `assets/images/brands/coca-cola-logo.png`. Falls back to the neutral
   * white-label logo when omitted (no broken image).
   */
  logoPath?: string;
  /**
   * Wide banner shown behind the card header. Optional, same convention:
   * `assets/images/brands/coca-cola-banner.webp`. Falls back to the neutral
   * white-label banner when omitted.
   */
  bannerPath?: string;
}

/**
 * Curated grocery brands for demos. Add a `logoPath` / `bannerPath` per brand
 * as you drop the asset files; until then each brand falls back to the neutral
 * white-label logo and banner.
 */
export const REAL_BRANDS: DemoBrand[] = [
  {
    name: 'Coca-Cola',
    label: {
      en: 'All Coca-Cola soft drinks',
      fr: 'Toutes les boissons Coca-Cola',
      es: 'Todas las bebidas Coca-Cola',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/coca-cola-banner.webp',
  },
  {
    name: 'Tropicana',
    label: {
      en: 'Tropicana fruit juices',
      fr: 'Les jus de fruits Tropicana',
      es: 'Zumos de fruta Tropicana',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/tropicana-banner.webp',
  },
  {
    name: 'Barilla',
    label: {
      en: 'Barilla pasta & sauces',
      fr: 'Pâtes et sauces Barilla',
      es: 'Pasta y salsas Barilla',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/barilla-banner.webp',
  },
  {
    name: 'Nutella',
    label: {
      en: 'Nutella spreads',
      fr: 'Pâtes à tartiner Nutella',
      es: 'Cremas para untar Nutella',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/nutella-banner.webp',
  },
  {
    name: 'Heinz',
    label: {
      en: 'Heinz ketchup & sauces',
      fr: 'Ketchup et sauces Heinz',
      es: 'Kétchup y salsas Heinz',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/heinz-banner.webp',
  },
  {
    name: "Kellogg's",
    label: {
      en: "Kellogg's breakfast cereals",
      fr: "Céréales Kellogg's",
      es: "Cereales Kellogg's",
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/kelloggs-banner.webp',
  },
  {
    name: 'Danone',
    label: {
      en: 'Danone yogurts & dairy',
      fr: 'Yaourts et produits laitiers Danone',
      es: 'Yogures y lácteos Danone',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/danone-banner.webp',
  },
  {
    name: 'Nestlé',
    label: {
      en: 'Nestlé groceries',
      fr: 'Épicerie Nestlé',
      es: 'Comestibles Nestlé',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/nestle-banner.webp',
  },
  {
    name: 'Lipton',
    label: {
      en: 'Lipton teas & infusions',
      fr: 'Thés et infusions Lipton',
      es: 'Tés e infusiones Lipton',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/lipton-banner.webp',
  },
  {
    name: 'Pringles',
    label: {
      en: 'Pringles crisps',
      fr: 'Chips Pringles',
      es: 'Patatas Pringles',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/pringles-banner.webp',
  },
  {
    name: 'Evian',
    label: {
      en: 'Evian mineral water',
      fr: 'Eau minérale Evian',
      es: 'Agua mineral Evian',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/evian-banner.webp',
  },
  {
    name: 'Oreo',
    label: {
      en: 'Oreo biscuits',
      fr: 'Biscuits Oreo',
      es: 'Galletas Oreo',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/oreo-banner.webp',
  },
  {
    name: 'Lavazza',
    label: {
      en: 'Lavazza coffee',
      fr: 'Café Lavazza',
      es: 'Café Lavazza',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/lavazza-banner.webp',
  },
  {
    name: "Lay's",
    label: {
      en: "Lay's crisps",
      fr: "Chips Lay's",
      es: "Patatas Lay's",
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/lays-banner.webp',
  },
  {
    name: 'Activia',
    label: {
      en: 'Activia yogurts',
      fr: 'Yaourts Activia',
      es: 'Yogures Activia',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/activia-banner.webp',
  },
  {
    name: 'Perrier',
    label: {
      en: 'Perrier sparkling water',
      fr: 'Eau pétillante Perrier',
      es: 'Agua con gas Perrier',
    },
    description: EMPTY_BRAND_DESCRIPTION,
    bannerPath: 'assets/images/brands/perrier-banner.webp',
  },
];

/** Real grocery products for the product-check / scanner mock. */
export const REAL_PRODUCTS: string[] = [
  'Coca-Cola Original 1.5L',
  'Coca-Cola Zero 1.5L',
  'Tropicana Orange 1L',
  'Tropicana Multifruit 1L',
  'Barilla Spaghetti n.5 500g',
  'Barilla Penne Rigate 500g',
  'Nutella 750g',
  'Heinz Tomato Ketchup 570g',
  "Kellogg's Corn Flakes 500g",
  'Danone Activia Nature 4x125g',
  'Nestlé Dessert Noir 170g',
  'Lipton Yellow Label 50 bags',
  'Pringles Original 175g',
  'Evian 6x1.5L',
  'Oreo Original 154g',
  'Lavazza Qualità Rossa 250g',
  "Lay's Classic 150g",
  'Perrier 6x1L',
];

/** Pick a stable brand for a given index (e.g. an offerId), cycling the list. */
export const pickRealBrand = (index: number): DemoBrand =>
  REAL_BRANDS[index % REAL_BRANDS.length];

/** Brand logo path, falling back to the neutral white-label logo. */
export const brandLogoPath = (brand: DemoBrand): string =>
  brand.logoPath ?? FALLBACK_LOGO_PATH;

/** Brand banner path, falling back to the neutral white-label banner. */
export const brandBannerPath = (brand: DemoBrand): string =>
  brand.bannerPath ?? FALLBACK_BANNER_PATH;
